import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { ExploreItem, ExploreItemWithDistance } from "@/types/explore";
import { distanceKm } from "@/lib/map/distance";

export interface PlaceSearchFilters {
  q?: string;
  regions?: string[];
  cities?: string[];
  tags?: string[];
  userLat?: number;
  userLng?: number;
  sort?: "standard" | "distance";
  pageSize?: number;
}

export interface PlaceSearchResult {
  items: ExploreItemWithDistance[];
  total_count: number;
}

export interface PlaceFacets {
  regions: { value: string; count: number }[];
  cities: { value: string; count: number }[];
  tags: { value: string; count: number }[];
  total_count: number;
}

/**
 * Cover images come from media_assets, not from entity fields.
 */
async function searchPlaces(filters: PlaceSearchFilters): Promise<PlaceSearchResult> {
  let query = supabase
    .from("places_explore")
    .select("id, slug, name, city, region, tags, description_short, lat, lng, verified_at, producer_count", { count: "exact" })
    .order("name", { ascending: true });

  if (filters.q && filters.q.trim()) {
    const searchTerm = `%${filters.q.trim()}%`;
    query = query.or(`name.ilike.${searchTerm},description_short.ilike.${searchTerm}`);
  }
  if (filters.regions && filters.regions.length > 0) {
    query = query.in("region", filters.regions);
  }
  if (filters.cities && filters.cities.length > 0) {
    query = query.in("city", filters.cities);
  }
  if (filters.tags && filters.tags.length > 0) {
    query = query.overlaps("tags", filters.tags);
  }

  const limit = filters.pageSize || 100;
  query = query.limit(limit);

  const { data, error, count } = await query;
  if (error) { console.error("Error searching places:", error); throw error; }
  if (!data) return { items: [], total_count: 0 };

  let items: ExploreItemWithDistance[] = data.map((row: any) => {
    const subtitle = [row.city, row.region].filter(Boolean).join(", ");
    let _distanceKm: number | undefined;
    if (filters.userLat && filters.userLng && row.lat && row.lng) {
      _distanceKm = distanceKm(filters.userLat, filters.userLng, row.lat, row.lng);
    }
    return {
      id: String(row.id),
      kind: "place" as const,
      slug: row.slug || null,
      name: row.name || "",
      subtitle,
      shortDescription: row.description_short || null,
      tags: (row.tags || []) as string[],
      lat: row.lat ?? null,
      lng: row.lng ?? null,
      coverImageUrl: null, // Resolved from media_assets
      verified: !!row.verified_at,
      href: `/place/${row.slug}`,
      _distanceKm,
      producerCount: row.producer_count || 0,
    };
  });

  if (filters.sort === "distance" && filters.userLat && filters.userLng) {
    items = items.sort((a, b) => (a._distanceKm ?? Infinity) - (b._distanceKm ?? Infinity));
  }

  return { items, total_count: count || items.length };
}

export function usePlacesSearch(filters: PlaceSearchFilters, enabled = true) {
  return useQuery({
    queryKey: ["places-search", filters],
    queryFn: () => searchPlaces(filters),
    staleTime: 30_000,
    enabled,
  });
}

async function fetchPlaceFacets(filters: PlaceSearchFilters): Promise<PlaceFacets> {
  const { data, error } = await supabase
    .from("places_explore")
    .select("region, city, tags");
  if (error) { console.error("Error fetching place facets:", error); throw error; }

  const regions: Record<string, number> = {};
  const cities: Record<string, number> = {};
  const tags: Record<string, number> = {};

  (data || []).forEach((row: any) => {
    if (row.region) regions[row.region] = (regions[row.region] || 0) + 1;
    if (row.city) cities[row.city] = (cities[row.city] || 0) + 1;
    if (row.tags && Array.isArray(row.tags)) {
      row.tags.forEach((tag: string) => { tags[tag] = (tags[tag] || 0) + 1; });
    }
  });

  return {
    regions: Object.entries(regions).map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count),
    cities: Object.entries(cities).map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count),
    tags: Object.entries(tags).map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count),
    total_count: data?.length || 0,
  };
}

export function usePlaceFacets(filters: PlaceSearchFilters, enabled = true) {
  return useQuery({
    queryKey: ["places-facets", filters],
    queryFn: () => fetchPlaceFacets(filters),
    staleTime: 30_000,
    enabled,
  });
}
